// =====================================================================
// src/lib/v2/cart.js
//
// Per-business cart persistence using localStorage. The Cart screen and
// the BusinessProfile "+ Add" buttons share this so that:
//   - Items added from BusinessProfile show up on the Cart screen
//   - Cart state survives page refresh and tab navigation
//   - Each business has an isolated cart (key includes businessId)
//
// Storage shape:
//   key:   "aangan-cart-v1:{businessId}"
//   value: JSON.stringify([{ productId: "abc", qty: 2 }, ...])
//
// We only persist productId + qty here. Product name/price/image are
// hydrated from /businesses/{businessId}/products at read time so that
// the cart never shows stale prices.
//
// Failure-safe: every operation is wrapped in try/catch — incognito
// modes or full disk gracefully fall back to no-op behaviour.
// =====================================================================

const KEY_PREFIX = 'aangan-cart-v1:';

function key(businessId) {
  return KEY_PREFIX + businessId;
}

/**
 * Returns the cart for a business as an array of {productId, qty}.
 * Empty array if no cart exists or storage unreadable.
 */
export function getCart(businessId) {
  if (!businessId) return [];
  try {
    const raw = localStorage.getItem(key(businessId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: filter out malformed entries
    return parsed.filter(item =>
      item && typeof item.productId === 'string' &&
      Number.isFinite(item.qty) && item.qty > 0
    );
  } catch {
    return [];
  }
}

/**
 * Adds 1 to qty of productId in businessId's cart. If productId is not
 * yet in cart, inserts it with qty=1. Returns the new total cart size
 * (sum of all qty values).
 */
export function addToCart(businessId, productId) {
  if (!businessId || !productId) return 0;
  const cart = getCart(businessId);
  const existing = cart.find(i => i.productId === productId);
  if (existing) {
    existing.qty += 1;
  } else {
    cart.push({ productId, qty: 1 });
  }
  writeCart(businessId, cart);
  return cart.reduce((s, i) => s + i.qty, 0);
}

/**
 * Replaces the cart for a business with the given items array.
 * Used by the Cart screen when the user adjusts quantities or removes
 * items. Pass an empty array (or call clearCart) to empty.
 */
export function setCart(businessId, items) {
  if (!businessId) return;
  const cleaned = (Array.isArray(items) ? items : [])
    .filter(i => i && i.productId && Number.isFinite(i.qty) && i.qty > 0)
    .map(i => ({ productId: i.productId, qty: i.qty }));
  writeCart(businessId, cleaned);
}

/**
 * Empties the cart for a business. Called after a successful order.
 */
export function clearCart(businessId) {
  if (!businessId) return;
  try {
    localStorage.removeItem(key(businessId));
  } catch { /* no-op */ }
}

/**
 * Convenience: total item count (sum of all qty) for a business.
 * Returns 0 if cart empty / unreadable.
 */
export function getCartCount(businessId) {
  return getCart(businessId).reduce((s, i) => s + i.qty, 0);
}

function writeCart(businessId, cart) {
  try {
    if (cart.length === 0) {
      localStorage.removeItem(key(businessId));
    } else {
      localStorage.setItem(key(businessId), JSON.stringify(cart));
    }
  } catch { /* no-op */ }
}
